import Navbar from 'react-bootstrap/Navbar';
import NavDropdown from 'react-bootstrap/NavDropdown';
import Nav from 'react-bootstrap/Nav';
import { auth, updateUser } from '../firebase/firebase';
import { useAuthState, useSignOut } from 'react-firebase-hooks/auth';
import { useTranslation } from 'react-i18next';
import LOGO from '../assets/memowrite_logo.svg';
import UserImage from '../assets/images/user.png';
import ExitIcon from '../assets/icons/exit.svg';
import BookIcon from '../assets/icons/book.svg';
import SettingsIcon from '../assets/icons/settings.svg';
import CartIcon from '../assets/icons/cart.svg';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUserData } from '../hooks/useUserData';
import PackageSvg from '../assets/icons/package.svg';
import { useShowBanner } from '../hooks/useShowBanner';

const LOCALES = {
  'en-US': { title: '🇺🇸 English US' },
  'en-GB': { title: '🇬🇧 English UK / Int' },
  'es-ES': { title: '🇪🇦 Español' },
  'es-MX': { title: '🇲🇽 Español MX / US' },
  'fr-FR': { title: '🇫🇷 Francais' },
  'fr-CA': { title: '🇨🇦 Francais CA' },
  de: { title: '🇩🇪 Deutsch' },
  it: { title: '🇮🇹 Italiano' },
};

const Navigation = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [user] = useAuthState(auth);
  const [signOut] = useSignOut(auth);
  const location = useLocation();
  const { userData } = useUserData();
  const { shouldShowBanner } = useShowBanner();

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const lang = queryParams.get('lang');

    if (lang && lang !== userData?.language) {
      i18n.changeLanguage(lang);
    }
  }, [location.search, i18n, userData?.language]);

  useEffect(() => {
    const userLanguage = userData?.language;

    const userLanguageIncludesUnderscore =
      typeof userLanguage === 'string' && userLanguage?.includes('_');

    if (
      (userLanguage && userLanguage !== i18n?.language) ||
      userLanguageIncludesUnderscore
    ) {
      let newLanguage = userLanguage;

      if (userLanguageIncludesUnderscore) {
        newLanguage = userLanguage.replace('_', '-');
      } else if (userLanguage === 'en') {
        newLanguage = 'en-US';
      } else if (userLanguage === 'es') {
        newLanguage = 'es-ES';
      } else if (userLanguage === 'fr') {
        newLanguage = 'fr-FR';
      } else if (userLanguage === 'de') {
        newLanguage = 'de';
      } else if (userLanguage === 'it') {
        newLanguage = 'it';
      }

      i18n.changeLanguage(newLanguage);
      updateUser({
        uid: user?.uid,
        data: { language: newLanguage },
        email: user?.email,
      });
    }
  }, [userData?.language, i18n, user?.uid, user?.email]);

  const isAdmin = userData?.accessLevel === 'admin';

  const hasBookReadyForPrint = userData?.ready_for_print_book > 0;
  const hasCode = userData?.code;
  const displayShopLink = !hasBookReadyForPrint && hasCode;

  const handleLogout = () => {
    navigate('/');
    signOut();
  };

  const handleChangeLanguage = async (language) => {
    i18n.changeLanguage(language);
    await updateUser({
      uid: user?.uid,
      data: { language },
      email: user?.email,
    });
  };

  return (
    <div className="main-navigation">
      <Navbar collapseOnSelect expand="lg" className="main-navbar">
        <Navbar.Brand href="/" style={{ padding: '0 16px' }}>
          <img src={LOGO} alt="memowrite logo" />
        </Navbar.Brand>
        <Navbar.Toggle aria-controls="responsive-navbar-nav" />
        <Navbar.Collapse
          id="responsive-navbar-nav"
          className="justify-content-between"
          style={{
            width: '100%',
          }}
        >
          <div
            style={{
              alignItems: 'flex-start',
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
            }}
          >
            <NavDropdown
              title={LOCALES[i18n?.language]?.title}
              id="basic-nav-dropdown"
              style={{
                margin: '24px 16px 12px',
              }}
            >
              {Object.keys(LOCALES).map((locale) => (
                <NavDropdown.Item
                  key={LOCALES[locale]?.title}
                  onClick={() => handleChangeLanguage(locale)}
                >
                  {LOCALES[locale]?.title}
                </NavDropdown.Item>
              ))}
            </NavDropdown>
            {shouldShowBanner && (
              <a
                href="https://app.memowrite.com"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'block',
                  margin: '8px 16px 16px',
                  padding: '12px 14px',
                  background:
                    'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
                  borderRadius: '12px',
                  textDecoration: 'none',
                  position: 'relative',
                  overflow: 'hidden',
                  boxShadow: '0 4px 12px rgba(26, 26, 46, 0.2)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      background:
                        'linear-gradient(135deg, #ffc371 0%, #ff9a56 100%)',
                      borderRadius: 8,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <span style={{ fontSize: 28 }}>✨</span>
                  </div>
                  <div>
                    <div
                      style={{
                        color: '#ffffff',
                        fontSize: 12,
                        fontWeight: 600,
                        lineHeight: 1.3,
                        marginBottom: 2,
                      }}
                    >
                      {t('newDashboardAnnouncement.badge')}
                    </div>
                    <div
                      style={{
                        color: 'rgba(255, 255, 255, 0.7)',
                        fontSize: 11,
                        lineHeight: 1.3,
                      }}
                    >
                      {t(
                        'newDashboardAnnouncement.bannerSubtitle',
                        'Try the new experience'
                      )}
                    </div>
                  </div>
                </div>
              </a>
            )}
            <Nav
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                marginTop: '16px',
                padding: '4px 8px',
                width: '100%',
              }}
            >
              <Nav.Link
                style={{
                  backgroundColor:
                    location.pathname === '/' ? '#FFE693' : 'transparent',
                  color: '#182230',
                  display: 'flex',
                  borderRadius: '13px',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 15px',
                }}
                href="/"
              >
                <img src={BookIcon} alt="menu" />
                <p style={{ margin: 0 }}>{t('common.dashboard')}</p>
              </Nav.Link>
              {displayShopLink && (
                <Nav.Link
                  style={{
                    backgroundColor:
                      location.pathname === '/shop' ? '#FFE693' : 'transparent',
                    color: '#182230',
                    display: 'flex',
                    alignItems: 'center',
                    borderRadius: '13px',
                    gap: '8px',
                    padding: '10px 15px',
                  }}
                  href="/shop"
                >
                  <img src={CartIcon} alt="menu" />
                  <p style={{ margin: 0 }}>{t('common.shop')}</p>
                </Nav.Link>
              )}
              <Nav.Link
                style={{
                  backgroundColor:
                    location.pathname === '/profile'
                      ? '#FFE693'
                      : 'transparent',
                  color: '#182230',
                  display: 'flex',
                  borderRadius: '13px',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 15px',
                }}
                href="/profile"
              >
                <img src={SettingsIcon} alt="menu" />
                <p style={{ margin: 0 }}>{t('common.profile')}</p>
              </Nav.Link>
              {isAdmin && (
                <Nav.Link
                  style={{
                    backgroundColor:
                      location.pathname === '/admin'
                        ? '#FFE693'
                        : 'transparent',
                    color: '#182230',
                    display: 'flex',
                    borderRadius: '13px',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '10px 15px',
                  }}
                  href="/admin"
                >
                  <img src={PackageSvg} width={20} height={20} alt="menu" />
                  <p style={{ margin: 0 }}>{t('common.admin')}</p>
                </Nav.Link>
              )}
            </Nav>
            <div className="navbar-logout">
              <img
                style={{ width: '40px', height: '40px' }}
                src={UserImage}
                alt="user"
              />
              <div
                style={{ display: 'flex', flexDirection: 'column', gap: '0px' }}
              >
                <p
                  style={{
                    margin: 0,
                    width: '110px',
                    overflowX: 'hidden',
                    textOverflow: 'ellipsis',
                    fontWeight: 600,
                    fontSize: '14px',
                  }}
                >
                  {userData?.name}
                </p>
                <p
                  style={{
                    margin: 0,
                    width: '110px',
                    overflowX: 'hidden',
                    textOverflow: 'ellipsis',
                    fontSize: '14px',
                    color: '#666600',
                  }}
                >
                  {user?.email}
                </p>
              </div>
              <div onClick={handleLogout} style={{ cursor: 'pointer' }}>
                <img
                  style={{ width: '15px', height: '15px' }}
                  src={ExitIcon}
                  alt="sign out"
                />
              </div>
            </div>
          </div>
        </Navbar.Collapse>
      </Navbar>
    </div>
  );
};

export default Navigation;
